import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';

import {
  isPending,
  isWaitingForSignatures,
  isSignLaterEnabled,
  isConcluded,
} from '@flow/libs/agreement';
import {
  getCounterpartyParticipants,
  getAgreementMyParticipant,
  getSignedParticipants,
  usesSameDeviceSome,
  isAgreementOwner,
} from '@flow/libs/agreement/selectors';
import { hasPendingStateApprovers } from '@flow/libs/agreement/pending-state-flow';
import { isViewer } from '@flow/libs/agreement/participant';
import isInfluencer from '@flow/libs/agreement/participant/is-influencer';
import isExternalApprover from '@flow/libs/agreement/participant/is-external-approver';
import isOrganizer from '@flow/libs/agreement/participant/is-organizer';
import useAgreement from '@app/hooks/use-agreement';
import { isContractPristine } from '@app/reducers/current-contract';
import { setNotifyParticipant } from '@app/reducers/editor';
import { getAccountFromSessionSelector } from '@app/reducers/session';

import { isContractEditable } from '@app/components/document-layout-container/helpers';
import type { Layout } from '@app/components/document-layout-container/types';
import { PARTIALLY_SIGNED_TOAST_ID } from '@app/components/agreement-loader/partially-signed-toast';
import toast from '@app/components/toasts';
import { T } from '@app/components/translation';

import { ApprovePendingActions } from './approve-pending-actions';
import { SignButton } from './buttons-call-to-action/buttons';
import {
  DiscardSaveActions,
  DiscardSaveSignActions,
  SignOnSameDeviceActions,
} from './buttons-call-to-action';
import SignLaterEnabled from './states/sign-later-enabled';
import { showSavedAndNotifiedToast } from './save-changes-toasts';
import { useSaveAgreementHandler } from './buttons-call-to-action/hooks/use-save-agreement-handler';

type Props = {
  agreementId: Oneflow.Agreement['id'];
  layout: Layout;
};

const PendingActions = ({ agreementId, layout }: Props) => {
  const agreement = useAgreement(agreementId);
  const participant = getAgreementMyParticipant(agreement);
  const dispatch = useDispatch();

  const isPristine = useSelector(isContractPristine);
  const isEditable = isContractEditable(agreement);

  // We only want to notify participants if there are other parties than the owner party
  const shouldNotifyOtherParties = getCounterpartyParticipants(agreement).length > 0;

  const account = useSelector(getAccountFromSessionSelector);
  const someUsingSameDevice = usesSameDeviceSome(agreement, account);
  const canSeeSignLater = isAgreementOwner(account, agreement) && !isViewer(participant);

  const [showModal, setShowModal] = useState(false);

  const menuItems = [
    {
      content: (
        <T
          _str="Save and notify"
          _comment="Menu item for saving and notifying participants"
        />
      ),
      onSelect: () => {
        setShowModal(true);
      },
    },
  ];

  useEffect(() => {
    const isPartialSigned = getSignedParticipants(agreement).length > 0;
    if (!isPristine && isEditable && shouldNotifyOtherParties && isPartialSigned) {
      dispatch(setNotifyParticipant(true));
    }

    if (!isPartialSigned) {
      dispatch(setNotifyParticipant(false));
    }
  }, [
    isPristine,
    isEditable,
    dispatch,
    shouldNotifyOtherParties,
    agreement,
  ]);

  const onSuccess = () => {
    showSavedAndNotifiedToast();
    /**
     * Removes the "partially signed" toast when saving changes.
     * Saving resets signatures, so the document is no longer partially signed.
     * This prevents showing stale toast information to the user.
     */
    toast.remove({
      toastId: PARTIALLY_SIGNED_TOAST_ID,
    });
  };

  const { saveAgreementHandler: onSaveAndNotifyChanges } = useSaveAgreementHandler({
    agreementId,
    onSuccess,
  });

  if (isEmpty(participant)) {
    return null;
  }

  if (!isPristine && isEditable) {
    // We only want to notify participants if there are other parties than the owner party
    if (shouldNotifyOtherParties) {
      if (someUsingSameDevice) {
        return (
          <SignOnSameDeviceActions
            agreement={agreement}
            layout={layout}
          />
        );
      }

      if (isInfluencer(participant) || isOrganizer(participant)) {
        return (
          <DiscardSaveActions
            agreementId={agreementId}
            isSaveDropdown
            menuItems={menuItems}
            notifyParticipants={{
              setModalOpen: setShowModal,
              modalOpen: showModal,
              onSaveAndNotifyChanges,
            }}
          />
        );
      }

      if (isWaitingForSignatures(agreement)) {
        return (
          <DiscardSaveSignActions
            agreement={agreement}
            layout={layout}
          />
        );
      }

      return (
        <DiscardSaveSignActions
          agreement={agreement}
          isSaveDropdown
          menuItems={menuItems}
          layout={layout}
          notifyParticipants={{
            setModalOpen: setShowModal,
            modalOpen: showModal,
            onSaveAndNotifyChanges,
          }}
        />
      );
    }

    if (isInfluencer(participant) || isOrganizer(participant)) {
      return <DiscardSaveActions agreementId={agreementId} />;
    }

    if (isWaitingForSignatures(agreement) && someUsingSameDevice) {
      return (
        <SignOnSameDeviceActions
          agreement={agreement}
          layout={layout}
        />
      );
    }

    return (
      <DiscardSaveSignActions
        agreement={agreement}
        layout={layout}
      />
    );
  }

  if (
    hasPendingStateApprovers(agreement) &&
    isExternalApprover(participant) &&
    !isConcluded(agreement)
  ) {
    return (
      <ApprovePendingActions
        layout={layout}
        agreement={agreement}
        participant={participant}
      />
    );
  }

  if ((isInfluencer(participant) || isOrganizer(participant)) && !someUsingSameDevice) {
    if (isSignLaterEnabled(agreement) && canSeeSignLater) {
      return <SignLaterEnabled layout={layout} />;
    }

    return null;
  }

  if (someUsingSameDevice && isPending(agreement)) {
    return (
      <SignOnSameDeviceActions
        agreement={agreement}
        layout={layout}
      />
    );
  }

  return (
    <>
      <SignButton
        agreement={agreement}
        layout={layout}
      />
    </>
  );
};

export default PendingActions;
